import {
  ProviderMenuItemCategory,
  ProviderMenuItemDish,
  ProviderMenuItemOptionBase,
  ProviderMenuItemOptionGroupBase,
  ProviderMenuResponse,
} from '@provider-portal/common/api/types';
import { normalize, schema } from 'normalizr';
import { Omit } from 'utility-types';

export type MenuEntityId = number & { __type_id: never };

// special helper to replace normalized sub-entities with array of ids
export type NormalizeType<T, K extends keyof T> = Omit<T, K> & { [key in K]: MenuEntityId[] };

// we need to add parent id to an item, so in future we will be able to build path to an item with O(n)
// src/rdx/menuManagement/utils.ts - you can find getPathToItem here
const processStrategy = (entity: any, parent: any) => ({ ...entity, parentId: parent.id });

// Motivation https://github.com/paularmstrong/normalizr#quick-start
const option = new schema.Entity('option', {}, { processStrategy });

const optionGroup = new schema.Entity(
  'optionGroup',
  {
    options: [option],
  },
  { processStrategy },
);

const dish = new schema.Entity(
  'dish',
  {
    option_groups: [optionGroup],
  },
  { processStrategy },
);

const category = new schema.Entity('category', { dishes: [dish] }, { processStrategy });

const menu = { categories: [category] };

export type Category = NormalizeType<ProviderMenuItemCategory, 'dishes'>;
export type Dish = NormalizeType<ProviderMenuItemDish, 'option_groups'> & {
  parentId: MenuEntityId;
};
export type OptionGroup = NormalizeType<ProviderMenuItemOptionGroupBase, 'options'> & {
  parentId: MenuEntityId;
};
export type Option = ProviderMenuItemOptionBase & {
  parentId: MenuEntityId;
};

export interface NormalizedMenu {
  category: {
    [key: number]: Category | undefined;
  };
  dish: {
    [key: number]: Dish | undefined;
  };
  optionGroup: {
    [key: number]: OptionGroup | undefined;
  };
  option: {
    [key: number]: Option | undefined;
  };
}

interface NormalizedResult {
  dishes: MenuEntityId[];
  optionGroups: MenuEntityId[];
  options: MenuEntityId[];
  categories: MenuEntityId[];
}

export interface MenuScheme {
  entities: NormalizedMenu;
  result: NormalizedResult;
}

const toNumber = (v: string) => Number(v);

export const normalizeData = (payload: ProviderMenuResponse): MenuScheme => {
  const data = normalize(payload, menu);

  return {
    entities: data.entities as any, // normalizr types are not correct
    result: {
      // keys from object always strings, so we need to map to number
      dishes: data.entities.dish ? Object.keys(data.entities.dish).map(toNumber) : [],
      optionGroups: data.entities.optionGroup
        ? Object.keys(data.entities.optionGroup).map(toNumber)
        : [],
      options: data.entities.option ? Object.keys(data.entities.option).map(toNumber) : [],
      ...data.result,
    },
  };
};
